/**
 * Shopify Product Create Service
 * 
 * This service handles creating new products in Shopify instead of updating existing ones.
 * It processes products sequentially, creates new products, and archives old ones.
 */

import { QueueManagerService } from './queueManagerService';
import { ShopifyGraphQLService } from './shopifyGraphQLService';
import { ShopifyVariantService } from './shopifyVariantService';
import { ShopifyMediaService } from './shopifyMediaService';
import { ShopifyInventoryService } from './shopifyInventoryService';
import { ShopifyProductInputService } from './shopifyProductInputService';
import { ShopifyMetafieldService } from './shopifyMetafieldService';
import { ShopifySeoService } from './shopifySeoService';
import { ShopifyChangeService } from './shopifyChangeService';
import { ShopifyCollectionService } from './shopifyCollectionService';
import { ShopifyProductChangesService } from './shopifyProductChangesService';
import { ShopifyVariantImageTrackingService } from './shopifyVariantImageTrackingService';
import { ShopifyVariantHandlerService } from './shopifyVariantHandlerService';

export class ShopifyProductCreateService {
  constructor(shop, accessToken) {
    // Clean shop URL to just the domain
    const shopDomain = shop.replace(/^https?:\/\//, '').replace(/\/$/, '');
    this.shop = shopDomain;
    this.accessToken = accessToken;

    // Pass clean shop domain to all services
    this.graphqlService = new ShopifyGraphQLService(shopDomain, accessToken);
    this.variantService = new ShopifyVariantService(shopDomain, accessToken);
    this.mediaService = new ShopifyMediaService(shopDomain, accessToken);
    this.inventoryService = new ShopifyInventoryService(shopDomain, accessToken);
    
    // Initialize other services
    this.productInputService = new ShopifyProductInputService();
    this.metafieldService = new ShopifyMetafieldService(this.graphqlService);
    this.seoService = new ShopifySeoService(this.graphqlService);
    this.changeService = new ShopifyChangeService(this.graphqlService);
    this.collectionService = new ShopifyCollectionService(this.graphqlService);
    this.productChangesService = new ShopifyProductChangesService();
    this.variantImageTrackingService = new ShopifyVariantImageTrackingService();
    
    // Initialize variant handler service with proper options
    this.variantHandlerService = new ShopifyVariantHandlerService({
      shopifyService: this.graphqlService,
      shopifyLocationId: 'gid://shopify/Location/93645864994' // Default location ID
    });
  }

  /**
   * Prepares product input for the Shopify API
   * @param {object} product - Original product data
   * @param {object} changes - Changes to apply to the product
   * @returns {object} - Product input for Shopify API
   */
  prepareProductInput(product, changes = {}) {
    console.log('Preparing product input with changes:', Object.keys(changes));
    
    const input = {
      title: product.title,
      descriptionHtml: product.descriptionHtml || product.bodyHtml,
      vendor: product.vendor,
      productType: product.productType,
      tags: product.tags,
      status: 'DRAFT', // Set product as draft by default
      options: [],
      variants: [],
      images: [],
      metafields: [],
      originalVariants: [],
      originalVariantImageAssociations: {},
      skuChangeMap: {}, // Map to track SKU changes (old SKU to new SKU)
      variant_updates: changes.variant_updates || {} // Include variant updates from local edits
    };
    
    // First check if we have a product type update in changes
    if (changes && changes.productType) {
      input.productType = changes.productType;
    }
    console.log(`Product type: ${product.productType} -> ${input.productType}`);
    
    // Add debug logging for product structure
    console.log('Product structure:', {
      hasMetafields: !!product.metafields,
      metafieldFormat: product.metafields ? (product.metafields.edges ? 'edges' : (Array.isArray(product.metafields) ? 'array' : 'other')) : 'none',
      metafieldCount: product.metafields?.edges?.length || (Array.isArray(product.metafields) ? product.metafields.length : 0) || 0
    });
    
    // Extract options
    if (product.options && product.options.edges) {
      input.options = product.options.edges.map(edge => ({
        name: edge.node.name,
        values: edge.node.values,
        id: edge.node.id
      }));
    } else if (product.options && Array.isArray(product.options)) {
      input.options = product.options.map(option => ({
        name: option.name,
        values: option.values,
        id: option.id
      }));
    }
    
    // Ensure the updated values from changes.options are used
    input.options = input.options.map((option, index) => {
      let values = option.values;
      
      // If we have updated values from the UI, use those instead
      if (changes && 
          changes.options && 
          changes.options.length > index) {
        values = changes.options[index].values || values;
        console.log(`Using updated values for option ${option.name}:`, values);
      }
      
      console.log(`Formatting option ${option.name} with values:`, values);
      return {
        name: option.name,
        values: values.map(value => {
          console.log(`  - Value: ${value}`);
          return value; // Ensure each value is a string
        })
      };
    });
    
    // Extract images
    if (product.images) {
      let images = [];
      
      if (product.images.edges) {
        images = product.images.edges.map(edge => edge.node);
      } else if (Array.isArray(product.images)) {
        images = product.images;
      }
      
      // Process images and store variant associations
      input.images = images.map(image => {
        const src = image.url || image.originalSrc || image.src;
        if (!src || !this.isValidUrl(src)) {
          console.error('Invalid image URL:', src);
          return null; // Skip invalid URLs
        }
        const processedImage = {
          id: image.id,
          src: src,
          alt: image.altText || '',
          filename: src.split('/').pop(),
          variantIds: [],
          sku: null
        };
        
        // Store the original image ID for reference
        if (image.id) {
          input.originalVariantImageAssociations[image.id] = [];
        }
        
        return processedImage;
      }).filter(Boolean); // Remove null entries
      
      console.log(`Extracted ${input.images.length} images from product data`);
    }
    
    // Extract variants
    if (product.variants) {
      let variants = [];
      
      if (product.variants.edges) {
        variants = product.variants.edges.map(edge => edge.node);
      } else if (product.variants && Array.isArray(product.variants)) {
        variants = product.variants;
      }
      
      // Store original variants for reference
      input.originalVariants = variants.map(variant => {
        // Extract inventory quantities from various possible properties
        let inventoryQuantity = 0;
        
        if (variant.inventoryQuantity !== undefined) {
          inventoryQuantity = parseInt(variant.inventoryQuantity, 10);
        } else if (variant.inventory_quantity !== undefined) {
          inventoryQuantity = parseInt(variant.inventory_quantity, 10);
        } else if (variant.quantity !== undefined) {
          inventoryQuantity = parseInt(variant.quantity, 10);
        } else if (typeof variant.inventory === 'object' && variant.inventory !== null) {
          // Try to extract from inventory object if it exists
          inventoryQuantity = parseInt(variant.inventory.quantity || 0, 10);
        }
        
        // Ensure it's a number
        inventoryQuantity = isNaN(inventoryQuantity) ? 0 : inventoryQuantity;
        
        console.log(`Storing original variant with SKU ${variant.sku} and inventory quantity: ${inventoryQuantity}`);
        
        // Include image associations if available
        const imageIds = variant.image ? [variant.image.id] : [];
        
        // Check for image associations in different formats
        if (variant.imageIds && Array.isArray(variant.imageIds)) {
          imageIds.push(...variant.imageIds);
        } else if (variant.image_ids && Array.isArray(variant.image_ids)) {
          imageIds.push(...variant.image_ids);
        }
        
        // Include selected options in a consistent format
        const selectedOptions = variant.selectedOptions ? 
          variant.selectedOptions : 
          (variant.selected_options || []);
        
        return {
          id: variant.id,
          sku: variant.sku,
          options: variant.selectedOptions ? 
            variant.selectedOptions.map(option => option.value) : 
            variant.options || [],
          selectedOptions: selectedOptions,
          inventoryQuantity: inventoryQuantity,
          inventory_quantity: inventoryQuantity,
          imageIds: imageIds,
          image: variant.image
        };
      });
      
      // Format variants according to API 2025-01 spec
      const formattedVariants = variants.map((variant, index) => {
        // Extract price, compareAtPrice, and SKU from the variant
        const price = variant.price || '0.00';
        const compareAtPrice = variant.compareAtPrice || variant.comparePrice || null;
        const sku = variant.sku || '';
        
        // Check if there are UI updates for this variant
        let uiUpdates = null;
        if (changes && 
            changes.variant_updates && 
            variant.id && 
            changes.variant_updates[variant.id]) {
          uiUpdates = changes.variant_updates[variant.id];
          console.log(`Found UI updates for variant ${variant.id}:`, JSON.stringify(uiUpdates));
        }
        
        // For variants with optionValues (already in API format)
        if (variant.optionValues && Array.isArray(variant.optionValues)) {
          // First try to use as-is if format is correct
          const validFormat = variant.optionValues.every(opt => 
            typeof opt === 'object' && opt !== null &&
            typeof opt.optionName === 'string' && 
            typeof opt.name === 'string'
          );

          if (validFormat) {
            // Extract inventory quantity from the variant
            let inventoryQuantity = 0;
            
            if (variant.inventoryQuantity !== undefined) {
              inventoryQuantity = parseInt(variant.inventoryQuantity, 10);
            } else if (variant.inventory_quantity !== undefined) {
              inventoryQuantity = parseInt(variant.inventory_quantity, 10);
            } else if (variant.quantity !== undefined) {
              inventoryQuantity = parseInt(variant.quantity, 10);
            } else if (typeof variant.inventory === 'object' && variant.inventory !== null) {
              // Try to extract from inventory object if it exists
              inventoryQuantity = parseInt(variant.inventory.quantity || 0, 10);
            }
            
            // Ensure it's a number
            inventoryQuantity = isNaN(inventoryQuantity) ? 0 : inventoryQuantity;
            
            console.log(`Formatting variant with SKU ${sku} and inventory quantity: ${inventoryQuantity}`);
            
            return {
              optionValues: variant.optionValues,
              price,
              compareAtPrice,
              inventoryQuantity,
              inventory_quantity: inventoryQuantity,
              inventoryItem: {
                sku: sku || ""
              }
            };
          }
        }

        // For variants with selectedOptions (from Shopify API)
        if (variant.selectedOptions && Array.isArray(variant.selectedOptions)) {
          // Extract inventory quantity from the variant
          let inventoryQuantity = 0;
          
          if (variant.inventoryQuantity !== undefined) {
            inventoryQuantity = parseInt(variant.inventoryQuantity, 10);
          } else if (variant.inventory_quantity !== undefined) {
            inventoryQuantity = parseInt(variant.inventory_quantity, 10);
          } else if (variant.quantity !== undefined) {
            inventoryQuantity = parseInt(variant.quantity, 10);
          } else if (typeof variant.inventory === 'object' && variant.inventory !== null) {
            // Try to extract from inventory object if it exists
            inventoryQuantity = parseInt(variant.inventory.quantity || 0, 10);
          }
          
          // Ensure it's a number
          inventoryQuantity = isNaN(inventoryQuantity) ? 0 : inventoryQuantity;
          
          console.log(`Formatting variant with SKU ${sku} and inventory quantity: ${inventoryQuantity}`);
          
          return {
            optionValues: variant.selectedOptions.map(option => ({
              optionName: option.name,
              name: option.value
            })),
            price,
            compareAtPrice,
            inventoryQuantity,
            inventory_quantity: inventoryQuantity,
            inventoryItem: {
              sku: sku || ""
            }
          };
        }

        // For variants with options array (from frontend)
        if (variant.options && Array.isArray(variant.options) && product.options) {
          // Extract inventory quantity from the variant
          let inventoryQuantity = 0;
          
          if (variant.inventoryQuantity !== undefined) {
            inventoryQuantity = parseInt(variant.inventoryQuantity, 10);
          } else if (variant.inventory_quantity !== undefined) {
            inventoryQuantity = parseInt(variant.inventory_quantity, 10);
          } else if (variant.quantity !== undefined) {
            inventoryQuantity = parseInt(variant.quantity, 10);
          } else if (typeof variant.inventory === 'object' && variant.inventory !== null) {
            // Try to extract from inventory object if it exists
            inventoryQuantity = parseInt(variant.inventory.quantity || 0, 10);
          }
          
          // Ensure it's a number
          inventoryQuantity = isNaN(inventoryQuantity) ? 0 : inventoryQuantity;
          
          console.log(`Formatting variant with SKU ${sku} and inventory quantity: ${inventoryQuantity}`);
          
          return {
            optionValues: variant.options.map((value, i) => ({
              optionName: product.options[i].name,
              name: value
            })),
            price,
            compareAtPrice,
            inventoryQuantity,
            inventory_quantity: inventoryQuantity,
            inventoryItem: {
              sku: sku || ""
            }
          };
        }

        // Log the variant structure for debugging
        console.error('Invalid variant structure:', JSON.stringify(variant, null, 2));
        throw new Error(`Variant at index ${index} must have one of:
          1. optionValues array with {optionName, name}
          2. selectedOptions array with {name, value}
          3. options array with values matching product options
        `);
      });

      input.variants = formattedVariants;
    }
    
    // Extract existing metafields from the product
    if (product.metafields && product.metafields.edges && product.metafields.edges.length > 0) {
      console.log(`Found ${product.metafields.edges.length} existing metafields in product`);
      
      for (const edge of product.metafields.edges) {
        const metafield = edge.node;
        // Check if this metafield is being updated in the changes
        const isBeingUpdated = changes && changes.metafield_updates && 
                              Object.keys(changes.metafield_updates).some(id => id === metafield.id);
        
        // Only add if not being updated (updates are handled separately)
        if (!isBeingUpdated) {
          // Add to metafields array if not already present
          const existingIndex = input.metafields.findIndex(m => 
            m.namespace === metafield.namespace && m.key === metafield.key
          );
          
          if (existingIndex === -1) {
            input.metafields.push({
              namespace: metafield.namespace,
              key: metafield.key,
              value: metafield.value,
              type: metafield.type || 'string' // Default to string if type not specified
            });
            console.log(`Added existing metafield: ${metafield.namespace}.${metafield.key} = ${metafield.value}`);
          }
        }
      }
    } else if (product.metafields && Array.isArray(product.metafields) && product.metafields.length > 0) {
      console.log(`Found ${product.metafields.length} existing metafields in product (array format)`);
      
      for (const metafield of product.metafields) {
        // Check if this metafield is being updated in the changes
        const isBeingUpdated = changes && changes.metafield_updates && 
                              Object.keys(changes.metafield_updates).some(id => id === metafield.id);
        
        // Only add if not being updated (updates are handled separately)
        if (!isBeingUpdated) {
          // Add to metafields array if not already present
          const existingIndex = input.metafields.findIndex(m => 
            m.namespace === metafield.namespace && m.key === metafield.key
          );
          
          if (existingIndex === -1) {
            input.metafields.push({
              namespace: metafield.namespace,
              key: metafield.key,
              value: metafield.value,
              type: metafield.type || 'string' // Default to string if type not specified
            });
            console.log(`Added existing metafield: ${metafield.namespace}.${metafield.key} = ${metafield.value}`);
          }
        }
      }
    }
    
    // Process any changes
    if (changes) {
      // Validate changes
      const changeValidation = this.changeService.validateChanges(changes);
      if (!changeValidation.isValid) {
        throw new Error(`Invalid changes: ${changeValidation.errors.join(', ')}`);
      }

      // Process option changes
      input.options = this.changeService.processOptionChanges(input.options, changes);

      // Process variant changes
      const { variants: updatedVariants, skuChangeMap } = this.changeService.processVariantChanges(input.variants, changes);
      input.variants = updatedVariants;

      // Process variant option changes
      input.variants = this.changeService.processVariantOptionChanges(input.variants, input.options, changes);

      // Process variant image changes
      const imageAssociations = this.changeService.processVariantImageChanges(input.variants, changes);
      Object.assign(input.originalVariantImageAssociations, imageAssociations);
    }
    
    // Apply changes
    if (changes) {
      console.log('Applying changes to product input:', JSON.stringify(changes, null, 2));
      
      // Special handling for product type to ensure it's updated correctly
      // Check for both 'productType' and 'product_type' in changes
      if (changes.productType !== undefined) {
        input.productType = changes.productType;
        console.log(`Updated product type to: ${changes.productType}`);
      } else if (changes.product_type !== undefined) {
        input.productType = changes.product_type;
        console.log(`Updated product type from product_type to: ${changes.product_type}`);
      }
      
      // Special handling for image alt text
      if (changes.image_alt_text !== undefined) {
        // Apply the updated alt text to all images
        input.images.forEach(image => {
          image.alt = changes.image_alt_text;
        });
        console.log(`Updated alt text for all images to: ${changes.image_alt_text}`);
      }
      
      // Special handling for option names
      if (changes.option1_name !== undefined && input.options.length >= 1) {
        input.options[0].name = changes.option1_name;
        console.log(`Updated option 1 name to: ${changes.option1_name}`);
      }
      
      if (changes.option2_name !== undefined && input.options.length >= 2) {
        input.options[1].name = changes.option2_name;
        console.log(`Updated option 2 name to: ${changes.option2_name}`);
      }
      
      if (changes.option3_name !== undefined && input.options.length >= 3) {
        input.options[2].name = changes.option3_name;
        console.log(`Updated option 3 name to: ${changes.option3_name}`);
      }
      
      // Special handling for option values
      if (changes.option1_values && input.options.length >= 1) {
        // Option values are stored as an object with indices as keys
        Object.entries(changes.option1_values).forEach(([index, value]) => {
          const valueIndex = parseInt(index, 10);
          if (!isNaN(valueIndex) && valueIndex >= 0 && valueIndex < input.options[0].values.length) {
            input.options[0].values[valueIndex] = value;
            console.log(`Updated option 1 value at index ${valueIndex} to: ${value}`);
          }
        });
      }
      
      if (changes.option2_values && input.options.length >= 2) {
        // Option values are stored as an object with indices as keys
        Object.entries(changes.option2_values).forEach(([index, value]) => {
          const valueIndex = parseInt(index, 10);
          if (!isNaN(valueIndex) && valueIndex >= 0 && valueIndex < input.options[1].values.length) {
            input.options[1].values[valueIndex] = value;
            console.log(`Updated option 2 value at index ${valueIndex} to: ${value}`);
          }
        });
      }
      
      if (changes.option3_values && input.options.length >= 3) {
        // Option values are stored as an object with indices as keys
        Object.entries(changes.option3_values).forEach(([index, value]) => {
          const valueIndex = parseInt(index, 10);
          if (!isNaN(valueIndex) && valueIndex >= 0 && valueIndex < input.options[2].values.length) {
            input.options[2].values[valueIndex] = value;
            console.log(`Updated option 3 value at index ${valueIndex} to: ${value}`);
          }
        });
      }
      
      Object.entries(changes).forEach(([key, value]) => {
        // Skip null or undefined values
        if (value === null || value === undefined) {
          return;
        }
        
        // Handle other product fields
        if (key === 'title') input.title = value;
        if (key === 'descriptionHtml' || key === 'body_html') input.descriptionHtml = value;
        // productType is already handled above
        if (key === 'vendor') input.vendor = value;
        if (key === 'tags') input.tags = value;
        if (key === 'seo_title') {
          if (!input.seo) input.seo = {};
          input.seo.title = value;
        }
        if (key === 'seo_description') {
          if (!input.seo) input.seo = {};
          input.seo.description = value;
        }
        
        // Handle option name updates
        if (key === 'option_name_updates' && input.options.length > 0) {
          console.log('Applying option name updates:', value);
          Object.entries(value).forEach(([optionId, newName]) => {
            // Find the option by ID
            const optionIndex = input.options.findIndex(opt => opt.id === optionId);
            if (optionIndex !== -1) {
              console.log(`Updating option name from "${input.options[optionIndex].name}" to "${newName}"`);
              input.options[optionIndex].name = newName;
            }
          });
        }
        
        // Handle option value updates
        if (key === 'option_value_updates' && input.options.length > 0) {
          console.log('Applying option value updates:', value);
          Object.entries(value).forEach(([optionId, valueUpdates]) => {
            // Find the option by ID
            const optionIndex = input.options.findIndex(opt => opt.id === optionId);
            if (optionIndex !== -1) {
              Object.entries(valueUpdates).forEach(([oldValue, newValue]) => {
                // Find the value in the option's values array
                const valueIndex = input.options[optionIndex].values.findIndex(v => 
                  typeof v === 'string' ? v === oldValue : v.name === oldValue
                );
                
                if (valueIndex !== -1) {
                  console.log(`Updating option value from "${oldValue}" to "${newValue}"`);
                  
                  // Update the value based on its format (string or object with name property)
                  if (typeof input.options[optionIndex].values[valueIndex] === 'string') {
                    input.options[optionIndex].values[valueIndex] = newValue;
                  } else {
                    input.options[optionIndex].values[valueIndex].name = newValue;
                  }
                  
                  // Also update any variants that use this option value
                  input.variants.forEach(variant => {
                    if (variant.options[optionIndex] === oldValue) {
                      console.log(`Updating variant option value from "${oldValue}" to "${newValue}"`);
                      variant.options[optionIndex] = newValue;
                    }
                  });
                }
              });
            }
          });
        }
      });
    }
    
    // Apply metafield updates
    if (changes && changes.metafield_updates) {
      if (!input.metafields) input.metafields = [];
      
      console.log(`Processing ${Object.keys(changes.metafield_updates).length} metafield updates`);
      
      Object.entries(changes.metafield_updates).forEach(([metafieldId, metafieldValue]) => {
        // Find the metafield in the original product
        const metafield = product.metafields?.edges?.find(edge => edge.node.id === metafieldId)?.node;
        
        if (metafield) {
          // Update existing metafield or add new one
          const existingIndex = input.metafields.findIndex(m => 
            m.namespace === metafield.namespace && m.key === metafield.key
          );
          
          if (existingIndex === -1) {
            input.metafields.push({
              namespace: metafield.namespace,
              key: metafield.key,
              value: metafieldValue,
              type: metafield.type || 'string' // Default to string if type not specified
            });
            console.log(`Added updated metafield: ${metafield.namespace}.${metafield.key} = ${metafieldValue}`);
          } else {
            input.metafields[existingIndex].value = metafieldValue;
            console.log(`Updated metafield: ${metafield.namespace}.${metafield.key} = ${metafieldValue}`);
          }
        }
      });
    }

    // Apply variant updates
    if (changes && changes.variant_updates && input.variants.length > 0) {
      // Create a map of variant IDs to their index in the variants array
      const variantMap = {};
      
      // Initialize the SKU change map if it doesn't exist
      if (!input.skuChangeMap) {
        input.skuChangeMap = {};
      }
      
      // Initialize the SKU image map if it doesn't exist
      if (!input.skuImageMap) {
        input.skuImageMap = {};
      }
      
      // Map variant IDs to their index in the variants array
      if (product.variants && product.variants.edges) {
        product.variants.edges.forEach((edge, index) => {
          if (edge.node && edge.node.id) {
            variantMap[edge.node.id] = index;
          }
        });
      } else if (product.variants && Array.isArray(product.variants)) {
        product.variants.forEach((variant, index) => {
          variantMap[variant.id] = index;
        });
      }
      
      // Create a map of SKUs to variant IDs
      const skuMap = {};
      
      // Map SKUs to their index in the variants array
      if (product.variants && product.variants.edges) {
        product.variants.edges.forEach((edge, index) => {
          if (edge.node && edge.node.id && edge.node.sku) {
            skuMap[edge.node.sku] = edge.node.id;
          }
        });
      } else if (product.variants && Array.isArray(product.variants)) {
        product.variants.forEach((variant, index) => {
          if (variant.id && variant.sku) {
            skuMap[variant.sku] = variant.id;
          }
        });
      }
      
      // Create a map of variant IDs to their option combinations
      const variantOptionCombinations = new Map();
      
      // Map variant IDs to their option combinations
      if (product.variants && product.variants.edges) {
        product.variants.edges.forEach((edge, index) => {
          if (edge.node && edge.node.id && edge.node.selectedOptions) {
            const optionCombo = edge.node.selectedOptions
              .map(option => option.value)
              .sort()
              .join('_');
            variantOptionCombinations.set(edge.node.id, optionCombo);
          }
        });
      } else if (product.variants && Array.isArray(product.variants)) {
        product.variants.forEach((variant, index) => {
          if (variant.id && variant.selectedOptions) {
            const optionCombo = variant.selectedOptions
              .map(option => option.value)
              .sort()
              .join('_');
            variantOptionCombinations.set(variant.id, optionCombo);
          }
        });
      }
      
      // Apply updates to each variant
      Object.entries(changes.variant_updates).forEach(([variantId, updates]) => {
        const variantIndex = variantMap[variantId];
        
        if (variantIndex !== undefined && input.variants[variantIndex]) {
          const originalSku = input.variants[variantIndex].sku;
          
          // Apply each update to the variant ONLY if it exists in the updates
          Object.entries(updates).forEach(([field, value]) => {
            // Skip null or undefined values
            if (value === null || value === undefined) {
              return;
            }
            
            input.variants[variantIndex][field] = value;
            
            // Track SKU changes for image association
            if (field === 'sku' && originalSku && value !== originalSku) {
              console.log(`Tracking SKU change: ${originalSku} -> ${value}`);
              
              // Add the SKU change mapping
              input.skuChangeMap[originalSku] = value;
              
              // If the original SKU had an image association, transfer it to the new SKU
              if (input.skuImageMap[originalSku]) {
                input.skuImageMap[value] = input.skuImageMap[originalSku];
                console.log(`Transferred image association from ${originalSku} to ${value}: ${input.skuImageMap[value]}`);
              }
            }
            
            // Handle inventory quantity changes
            if (field === 'inventoryQuantity' || field === 'inventory_quantity') {
              // Convert to integer
              const quantityValue = parseInt(value, 10);
              if (!isNaN(quantityValue)) {
                console.log(`Tracking inventory quantity change for variant ${variantIndex}: ${quantityValue}`);
                input.variants[variantIndex].inventoryQuantity = quantityValue;
                input.variants[variantIndex].inventory_quantity = quantityValue;
              }
            }
          });
        } else {
          console.log(`Warning: Variant with ID ${variantId} not found in product variants`);
        }
      });
    }
    
    console.log('Product input prepared with:', {
      options: input.options.length,
      variants: input.variants.length,
      images: input.images.length,
      metafields: input.metafields.length,
      originalVariants: input.originalVariants.length,
      variantImageAssociations: Object.keys(input.originalVariantImageAssociations).length,
      skuChanges: Object.keys(input.skuChangeMap).length
    });
    
    return input;
  }

  /**
   * Prepares local variant edits from UI changes
   * @param {object} changes - Changes from the UI
   * @param {Array} variants - Variant data
   * @returns {object} - Formatted local variant edits
   */
  prepareLocalVariantEdits(changes, variants) {
    if (!changes || !changes.variant_updates || !variants || variants.length === 0) {
      return {};
    }

    const localVariantEdits = {};

    // Process variant updates from changes
    Object.entries(changes.variant_updates).forEach(([variantId, updates]) => {
      // Find the variant in the original data
      const variant = variants.find(v => v.id === variantId);
      
      if (variant && variant.selectedOptions) {
        // Create a key based on option values
        const optionKey = variant.selectedOptions.map(opt => opt.value).join('-');
        
        // Store updates under this key
        localVariantEdits[optionKey] = updates;
        console.log(`Prepared local edits for variant with options ${optionKey}:`, updates);
      }
    });

    return localVariantEdits;
  }

  /**
   * Formats product options according to Shopify API 2025-01 specifications
   * @param {Object} productInput - The product input data
   * @returns {Array} Formatted product options
   */
  formatProductOptions(productInput) {
    return productInput.options.map(option => ({
      name: option.name,
      values: option.values.map(value => ({
        name: typeof value === 'string' ? value : value.name
      }))
    }));
  }

  /**
   * Formats metafields according to Shopify API 2025-01 specifications
   * @param {Object} productInput - The product input containing metafields
   * @returns {Array} Formatted metafields
   */
  formatMetafieldsForAPI(productInput) {
    // Helper function to determine the metafield type based on key name patterns
    const determineMetafieldType = (namespace, key) => {
      // Common patterns for multiline text fields
      const multilinePatterns = ['description', 'content', 'html', 'text', 'details', 'specs', 'specifications'];
      
      // Check if the key contains any multiline patterns
      if (multilinePatterns.some(pattern => key.toLowerCase().includes(pattern))) {
        console.log(`Detected multiline_text_field for ${namespace}.${key} based on key name pattern`);
        return 'multi_line_text_field';
      }
      
      // Default to single line text field
      return 'single_line_text_field';
    };
    
    // First, create a map of all existing metafields from the standard metafields array
    // This will contain the original metafield definitions with their correct types
    const metafieldsMap = new Map();
    
    // Add existing metafields to the map with their original types
    if (productInput.metafields && Array.isArray(productInput.metafields) && productInput.metafields.length > 0) {
      console.log(`Processing ${productInput.metafields.length} existing metafields`);
      productInput.metafields.forEach(metafield => {
        if (metafield.namespace && metafield.key) {
          const key = `${metafield.namespace}.${metafield.key}`;
          metafieldsMap.set(key, {
            namespace: metafield.namespace,
            key: metafield.key,
            value: metafield.value,
            type: metafield.type || 'string' // Use the original type or default
          });
        }
      });
    }
    
    // Update values from metafields_array but PRESERVE the original types
    if (productInput.metafields_array && Array.isArray(productInput.metafields_array) && productInput.metafields_array.length > 0) {
      console.log(`Updating values from ${productInput.metafields_array.length} edited metafields in metafields_array`);
      productInput.metafields_array.forEach(metafield => {
        if (metafield.namespace && metafield.key) {
          const key = `${metafield.namespace}.${metafield.key}`;
          // If the metafield already exists, update its value but keep the original type
          if (metafieldsMap.has(key)) {
            const existingMetafield = metafieldsMap.get(key);
            existingMetafield.value = metafield.value;
            metafieldsMap.set(key, existingMetafield);
            console.log(`Updated metafield: ${key} value to: ${metafield.value} (keeping original type: ${existingMetafield.type})`);
          } else {
            // For new metafields, use the type from metafields_array or determine it
            metafieldsMap.set(key, {
              namespace: metafield.namespace,
              key: metafield.key,
              value: metafield.value,
              type: metafield.type || 'single_line_text_field' // Default to single_line_text_field for safety
            });
            console.log(`Added new metafield ${key} with value: ${metafield.value} and type: ${metafield.type || 'single_line_text_field'}`);
          }
        }
      });
    }
    
    // Also check metafield_updates for any additional updates (values only)
    if (productInput.metafield_updates && typeof productInput.metafield_updates === 'object') {
      console.log(`Applying ${Object.keys(productInput.metafield_updates).length} metafield updates from metafield_updates`);
      Object.entries(productInput.metafield_updates).forEach(([key, value]) => {
        if (key.includes('.') && value !== undefined) {
          const [namespace, metafieldKey] = key.split('.');
          // Only update if the metafield exists in the map or if the value is not null/undefined
          if (metafieldsMap.has(key) || value !== null) {
            const existingMetafield = metafieldsMap.get(key) || {
              namespace,
              key: metafieldKey,
              type: 'single_line_text_field' // Default to single_line_text_field for safety
            };
            
            existingMetafield.value = value;
            metafieldsMap.set(key, existingMetafield);
            console.log(`Updated metafield ${key} with value: ${value} (type: ${existingMetafield.type})`);
          }
        }
      });
    }
    
    // Convert the map back to an array
    const formattedMetafields = Array.from(metafieldsMap.values());
    
    if (formattedMetafields.length > 0) {
      console.log(`Final metafields for API: ${formattedMetafields.length} metafields`);
      formattedMetafields.forEach(metafield => {
        console.log(`- ${metafield.namespace}.${metafield.key}: ${metafield.value} (${metafield.type})`);
      });
      return formattedMetafields;
    }
    
    console.log('No valid metafields found in product input');
    return undefined; // Return undefined to exclude from API call if no metafields
  }

  /**
   * Assigns images to variants based on option values and filenames
   * @param {Array} images - The images to assign
   * @param {Map} variantMap - Map of variant option combinations to variant IDs
   * @param {Map} skuMap - Map of SKUs to variant IDs
   * @param {Object} skuChanges - Map of SKU changes (old to new)
   * @param {Object} oldVariantMapping - Map of old variant names to new variant IDs (if available)
   * @returns {Map} - Map of media IDs to variant IDs for association
   */
  assignImagesToVariants(images, variantMap, skuMap, skuChanges = {}, oldVariantMapping = {}) {
    const imageVariantAssociations = new Map();
    
    // Ensure skuChanges is an object
    if (!skuChanges || typeof skuChanges !== 'object') {
      skuChanges = {};
    }
    
    // First, extract all option values from variant combinations for better matching
    const variantOptionValues = new Map(); // Map of variantId to its option values
    const variantOptionCombos = new Map(); // Map of variantId to its full option combination
    const variantOptionTypes = new Map(); // Map of variantId to option types (color, size, etc.)
    
    // Determine option names based on first variant's option names
    let optionNames = [];
    let firstCombo = '';
    for (const [optionCombo, variantId] of variantMap.entries()) {
      firstCombo = optionCombo;
      break;
    }
    
    // Extract option names from the first combination
    if (firstCombo) {
      const optionParts = firstCombo.split(' / ');
      // Store option names in their original order
      optionNames = optionParts.map((_, index) => `option${index + 1}`);
      console.log(`Detected ${optionNames.length} options: ${optionNames.join(', ')}`);
    }
    
    // Extract option values for each variant
    for (const [optionCombo, variantId] of variantMap.entries()) {
      const optionValues = optionCombo.split(' / ').map(value => value.trim().toLowerCase());
      variantOptionValues.set(variantId, optionValues);
      variantOptionCombos.set(variantId, optionCombo);
      
      // Create a map of option positions to values for this variant
      const optionTypeMap = {};
      optionValues.forEach((value, index) => {
        if (index < optionNames.length) {
          optionTypeMap[optionNames[index]] = value;
        }
      });
      variantOptionTypes.set(variantId, optionTypeMap);
    }
    
    // Group variants by each option value for easier matching
    const variantsByOption = new Map(); // Map of optionName -> optionValue -> variantIds[]
    
    // Initialize the structure for each option
    optionNames.forEach(optionName => {
      variantsByOption.set(optionName, new Map());
    });
    
    // Populate the structure
    for (const [variantId, optionTypeMap] of variantOptionTypes.entries()) {
      for (const [optionName, optionValue] of Object.entries(optionTypeMap)) {
        const optionMap = variantsByOption.get(optionName);
        if (!optionMap.has(optionValue)) {
          optionMap.set(optionValue, []);
        }
        optionMap.get(optionValue).push(variantId);
      }
    }
    
    // Process each image
    for (const image of images) {
      console.log(`Processing image: ${image.src || image.filename || "default_image_source"}`);
      const variantIds = new Set(); // Use Set to avoid duplicates
      
      // Case 1: Image has explicit variant associations from the original product
      if (image.variantIds && image.variantIds.length > 0) {
        console.log(`Image has ${image.variantIds.length} explicit variant associations`);
        variantIds.add(...image.variantIds);
        console.log(`Using explicit variant IDs: ${[...variantIds].join(', ')}`);
      }
      
      // Case 2: Image has variant option combinations
      else if (image.variantOptions && image.variantOptions.length > 0) {
        console.log(`Image has ${image.variantOptions.length} variant option combinations`);
        
        // Convert variant option combinations to actual variant IDs using the variantMap
        for (const optionCombo of image.variantOptions) {
          const variantId = variantMap.get(optionCombo);
          if (variantId) {
            variantIds.add(variantId);
            console.log(`Mapped option combination '${optionCombo}' to variant ID ${variantId}`);
          } else {
            console.warn(`Could not find variant ID for option combination: ${optionCombo}`);
          }
        }
      }
      
      // Case 3: Image has a SKU association
      else if (image.sku && skuMap.has(image.sku)) {
        const variantId = skuMap.get(image.sku);
        variantIds.add(variantId);
        console.log(`Associated image with variant ID ${variantId} based on SKU ${image.sku}`);
        
        // Check if this SKU was changed to a new one - safely access skuChanges
        const newSku = skuChanges[image.sku];
        if (newSku) {
          console.log(`Found SKU change mapping: ${image.sku} -> ${newSku}`);
          
          if (skuMap.has(newSku)) {
            const newVariantId = skuMap.get(newSku);
            variantIds.add(newVariantId);
            console.log(`Associating image with variant ${newVariantId} (matched by changed SKU ${image.sku} -> ${newSku})`);
          } else {
            console.log(`New SKU ${newSku} not found in skuMap, cannot associate image`);
          }
        }
      }
      
      // Case 4: Try to infer variant associations from the filename using enhanced logic
      else if (image.filename || image.src) {
        const filename = (image.filename || image.src).toLowerCase();
        console.log(`Trying to infer variant associations from filename: ${filename}`);
        
        // Track matches for each option type and their scores
        const optionMatches = new Map(); // Map of optionName -> Map(optionValue -> score)
        
        // Initialize option matches structure
        optionNames.forEach(optionName => {
          optionMatches.set(optionName, new Map());
        });
        
        // Check for matches for each option value in the filename
        for (const [optionName, optionValuesMap] of variantsByOption.entries()) {
          const matchesForOption = optionMatches.get(optionName);
          
          for (const [optionValue, variantIds] of optionValuesMap.entries()) {
            // Skip very short option values (less than 2 chars) to avoid false positives
            if (optionValue.length < 2) continue;
            
            // Check for exact word match with word boundaries
            const exactRegex = new RegExp(`\\b${optionValue}\\b`, 'i');
            if (exactRegex.test(filename)) {
              matchesForOption.set(optionValue, 100); // High score for exact match
              console.log(`Found exact match for option '${optionName}' value '${optionValue}' in filename`);
            }
            // Check for option value with different separators
            else {
              const valueWithDash = optionValue.replace(/ /g, '-');
              const valueWithUnderscore = optionValue.replace(/ /g, '_');
              
              if (filename.includes(valueWithDash) || filename.includes(valueWithUnderscore)) {
                matchesForOption.set(optionValue, 75); // Good score for separator match
                console.log(`Found match with separator for option '${optionName}' value '${optionValue}' in filename`);
              }
              // Check for partial match anywhere in the filename
              else if (filename.includes(optionValue)) {
                matchesForOption.set(optionValue, 50); // Medium score for partial match
                console.log(`Found partial match for option '${optionName}' value '${optionValue}' in filename`);
              }
            }
          }
        }
        
        // Find the best matches for each option
        const bestMatches = new Map(); // Map of optionName -> {value, score}
        
        for (const [optionName, matchesMap] of optionMatches.entries()) {
          let bestScore = 0;
          let bestValue = null;
          
          for (const [value, score] of matchesMap.entries()) {
            if (score > bestScore) {
              bestScore = score;
              bestValue = value;
            }
          }
          
          if (bestValue) {
            bestMatches.set(optionName, { value: bestValue, score: bestScore });
          }
        }
        
        // If we found matches for at least one option, use them to find matching variants
        if (bestMatches.size > 0) {
          console.log(`Found matches for ${bestMatches.size} options`);
          
          // Calculate a score for each variant based on how well it matches the filename
          const variantScores = new Map(); // Map of variantId -> score
          
          for (const [variantId, optionTypeMap] of variantOptionTypes.entries()) {
            let totalScore = 0;
            let matchCount = 0;
            
            // Check each option to see if it matches what we found in the filename
            for (const [optionName, optionValue] of Object.entries(optionTypeMap)) {
              const match = bestMatches.get(optionName);
              if (match && match.value === optionValue) {
                totalScore += match.score;
                matchCount++;
              }
            }
            
            // Only consider variants that match at least one option
            if (matchCount > 0) {
              // Calculate final score based on total score and percentage of options matched
              const percentageMatched = matchCount / bestMatches.size;
              const finalScore = totalScore * percentageMatched;
              
              variantScores.set(variantId, finalScore);
            }
          }
          
          // Find the highest score
          let highestScore = 0;
          for (const score of variantScores.values()) {
            if (score > highestScore) {
              highestScore = score;
            }
          }
          
          // Use variants with the highest score
          if (highestScore > 0) {
            for (const [variantId, score] of variantScores.entries()) {
              if (score === highestScore) {
                variantIds.add(variantId);
                console.log(`Assigned image to variant ${variantId} with match score ${score}`);
              }
            }
          }
          
          // If we only matched one option and it's the first option (usually color),
          // check if this is a "main" color image that should apply to all variants with that option
          if (bestMatches.size === 1 && bestMatches.has(optionNames[0])) {
            const firstOptionValue = bestMatches.get(optionNames[0]).value;
            const firstOptionVariants = variantsByOption.get(optionNames[0]).get(firstOptionValue) || [];
            
            // Check if the filename contains indicators that it's a main image
            const isMainImage = /main|primary|all|front|back|side|detail/i.test(filename);
            
            // If it seems like a main image or there are few variants with this option, assign to all
            if (isMainImage || firstOptionVariants.length <= 5) {
              for (const variantId of firstOptionVariants) {
                variantIds.add(variantId);
                console.log(`Assigned image to all variants with ${optionNames[0]}=${firstOptionValue}: ${variantId}`);
              }
            }
          }
        }
        
        // If no option matches, try to match by SKU as a last resort
        if (variantIds.size === 0) {
          for (const [sku, variantId] of skuMap.entries()) {
            if (filename.includes(sku.toLowerCase())) {
              variantIds.add(variantId);
              console.log(`Inferred variant ID ${variantId} from filename containing SKU: ${sku}`);
            }
          }
        }
      }
      
      // Store the image with its associated variant IDs
      if (variantIds.size > 0) {
        // Ensure we have unique variant IDs
        const uniqueVariantIds = [...new Set(variantIds)];
        imageVariantAssociations.set(image, uniqueVariantIds);
        console.log(`Assigned image to ${uniqueVariantIds.length} variants: ${[...uniqueVariantIds].join(', ')}`);
      } else {
        console.log(`No variant associations found for image`);
      }
    }
    
    return imageVariantAssociations;
  }

  /**
   * Prepares variant input data for API submission
   * @param {Array} variants - Original variants
   * @param {object} options - Updated options
   * @param {number} locationId - Default location ID
   * @param {object} productInput - The product input data containing variant_updates
   * @returns {Array} - Prepared variant inputs
   */
  prepareVariantInput(variants, options, locationId, productInput = {}) {
    return this.variantHandlerService.prepareVariantInput(variants, options, locationId, productInput);
  }

  /**
   * Gets the original option values from the product
   * @returns {Array} - Original option values
   */
  getOriginalOptionValues() {
    if (!this.originalProduct || !this.originalProduct.options) {
      return [];
    }
    
    return this.originalProduct.options.map(option => option.values);
  }

  /**
   * Gets the updated option values from the product input
   * @param {Object} productInput - The product input data
   * @returns {Array} The updated option values
   */
  getUpdatedOptionValues(productInput) {
    if (!productInput || !productInput.options) {
      return [];
    }
    
    // Ensure we extract the values correctly regardless of format
    return productInput.options.map(option => {
      return Array.isArray(option.values) 
        ? option.values.map(v => typeof v === 'string' ? v : v.name)
        : [];
    });
  }

  /**
   * Fetches a product by ID from Shopify
   * @param {string} productId - The product ID to fetch
   * @param {number} retries - Number of retry attempts
   * @returns {Promise<object>} - The product data
   */
  async fetchProductById(productId, retries = 3) {
    console.log(`Fetching product data (Attempt 1/${retries}): ${productId}`);
    
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const result = await this.graphqlService.makeGraphQLRequest({ query: `
          query getProduct($id: ID!) {
            product(id: $id) {
              id
              title
              handle
              status
              vendor
              productType
              descriptionHtml
              tags
              variants(first: 10) {
                edges {
                  node {
                    id
                    title
                    selectedOptions {
                      name
                      value
                    }
                  }
                }
              }
            }
            userErrors {
              field
              message
            }
          }
        `, variables: { id: productId } });
        
        if (result && result.product) {
          console.log(`Successfully fetched product: ${result.product.title}`);
          console.log(`Product has ${result.product.variants?.edges?.length || 0} variants, ${result.product.images?.edges?.length || 0} images`);
          
          // Log variant-image associations for debugging
          const variants = result.product.variants?.edges?.map(edge => edge.node) || [];
          variants.forEach((variant, index) => {
            console.log(`Variant ${index + 1} (${variant.title}):`);
            if (variant.image) {
              console.log(`- Direct image: ${variant.image.id}`);
            }
            if (variant.media && variant.media.edges) {
              console.log(`- Media items: ${variant.media.edges.length}`);
              variant.media.edges.forEach(edge => {
                console.log(`  - ${edge.node.mediaContentType}: ${edge.node.id}`);
              });
            }
          });
          
          return result.product;
        } else {
          console.error(`Failed to fetch product data, empty response`);
        }
      } catch (error) {
        console.error(`Error fetching product (attempt ${attempt}):`, error);
        
        if (attempt < retries) {
          const waitTime = 1000 * attempt; // Exponential backoff
          console.log(`Waiting ${waitTime / 1000} second${waitTime > 1000 ? 's' : ''} before retry...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }
    }
    
    console.error(`Failed to fetch product ${productId} after ${retries} attempts.`);
    return null;
  }

  /**
   * Creates a new product in Shopify
   * @param {object} productInput - The product input data
   * @param {object} changes - Changes to apply to the product
   * @returns {Promise<object>} - The created product data
   */
  async createProduct(productInput, changes = {}) {
    try {
      console.log('Creating product with input:', {
        title: productInput.title,
        options: productInput.options?.length || 0,
        variants: productInput.variants?.length || 0,
        images: productInput.images?.length || 0
      });
      
      // Initialize variant-image tracking if we have images and original variants
      if (productInput.images && productInput.images.length > 0 && 
          productInput.originalVariants && productInput.originalVariants.length > 0) {
        console.log('Initializing variant image tracking for product creation');
        this.variantImageTrackingService.initializeTracking(
          { variants: productInput.originalVariants },
          productInput.images
        );
      }
      
      // Apply any changes to the product input
      if (changes && Object.keys(changes).length > 0) {
        console.log('Applying changes to product input before creation:', Object.keys(changes));
        
        // Apply basic field changes ONLY if they exist in changes
        const basicFields = ['title', 'handle', 'descriptionHtml', 'body_html', 'productType', 'tags', 'vendor', 'image_alt_text'];
        basicFields.forEach(field => {
          if (changes[field] !== undefined) {
            // Special case for description which can be in either field
            if (field === 'bodyHtml' && changes[field]) {
              productInput.descriptionHtml = changes[field];
              console.log(`Updated description from bodyHtml`);
            } else if (field === 'image_alt_text') {
              productInput.imageAltText = changes[field];
              console.log(`Updated image alt text to: ${changes[field]}`);
              
              // Also update alt text in all images
              if (productInput.images && productInput.images.length > 0) {
                productInput.images.forEach(image => {
                  image.alt = changes[field];
                });
                console.log(`Updated alt text in ${productInput.images.length} images`);
              }
            } else if (field !== 'bodyHtml') { // Avoid duplicate assignment
              productInput[field] = changes[field];
              console.log(`Updated ${field} to:`, changes[field]);
            }
          }
        });
        
        // Apply option name changes ONLY if they exist in changes
        ['option1_name', 'option2_name', 'option3_name'].forEach((field, index) => {
          if (changes[field] !== undefined && productInput.options && productInput.options.length > index) {
            productInput.options[index].name = changes[field];
            console.log(`Updated option ${index + 1} name to: ${changes[field]}`);
          }
        });
        
        // Apply option value changes ONLY if they exist in changes
        ['option1_values', 'option2_values', 'option3_values'].forEach((field, index) => {
          if (changes[field] !== undefined && productInput.options && productInput.options.length > index) {
            Object.entries(changes[field]).forEach(([valueIndex, value]) => {
              const idx = parseInt(valueIndex, 10);
              if (!isNaN(idx) && idx >= 0 && idx < productInput.options[index].values.length) {
                productInput.options[index].values[idx] = value;
                console.log(`Updated option ${index + 1} value at index ${idx} to: ${value}`);
              }
            });
          }
        });
        
        // Apply changes using the product changes service to catch any other fields
        productInput = this.productChangesService.applyChanges(productInput, changes);
        
        // Prepare local variant edits from UI changes
        if (changes.variant_updates && productInput.originalVariants) {
          changes.localVariantEdits = this.prepareLocalVariantEdits(changes, productInput.originalVariants);
          console.log('Prepared local variant edits:', Object.keys(changes.localVariantEdits).length);
        }
      }
      
      // Proceed with product creation
      // Step 1: Create the product without options
      const mutation = `mutation productCreate($input: ProductInput!) {
        productCreate(input: $input) {
          product {
            id
            title
            handle
            status
            vendor
            productType
            descriptionHtml
            tags
          }
          userErrors {
            field
            message
          }
        }
      }`;

      // Format input according to API spec
      const variables = {
        input: {
          title: productInput.title,
          status: 'DRAFT',
          vendor: productInput.vendor,
          productType: productInput.productType,
          descriptionHtml: productInput.descriptionHtml,
          tags: productInput.tags,
          handle: productInput.handle || undefined, // Add handle to product creation
          metafields: this.formatMetafieldsForAPI(productInput) // Add metafields to product creation
        }
      };

      console.log('Step 1: Creating product without options:', {
        title: variables.input.title,
        productType: variables.input.productType,
        metafieldsCount: variables.input.metafields?.length || 0
      });

      const result = await this.graphqlService.mutate({ query: mutation, variables });

      if (result?.productCreate?.userErrors?.length > 0) {
        const errors = result.productCreate.userErrors;
        console.error('Error creating product:', errors);
        throw new Error(errors.map(e => `${e.field}: ${e.message}`).join(', '));
      }

      const product = result?.productCreate?.product;
      if (!product) {
        throw new Error('Failed to create product - no product data returned');
      }

      console.log('Product created successfully:', product.id);
      
      let allCreatedVariants = [];

      // Step 2: Add options to the product
      const optionsMutation = `mutation createOptions($productId: ID!, $options: [OptionCreateInput!]!) {
        productOptionsCreate(productId: $productId, options: $options) {
          userErrors {
            field
            message
            code
          }
          product {
            options {
              name
              linkedMetafield {
                namespace
                key
              }
              optionValues {
                name
                linkedMetafieldValue
              }
            }
          }
        }
      }`;

      // Format options according to API spec
      const options = this.formatProductOptions(productInput);
      console.log('Step 2: Creating product options with:', JSON.stringify({
        productId: product.id,
        options
      }, null, 2));

      // Skip if no options
      if (options.length > 0) {
        const optionsResult = await this.graphqlService.mutate({
          query: optionsMutation,
          variables: {
            productId: product.id,
            options: options
          }
        });

        console.log('Options creation response:', JSON.stringify(optionsResult, null, 2));

        if (optionsResult?.productOptionsCreate?.userErrors?.length > 0) {
          const errors = optionsResult.productOptionsCreate.userErrors;
          console.error('Error creating options:', errors);
          // Continue despite option errors - we'll log them but not fail the whole creation
          console.warn('Continuing product creation despite option errors');
        }

        console.log('Options added successfully to product:', product.id, JSON.stringify(options, null, 2));
      }

      // Step 3: Check if there's a default variant already
      console.log('Step 3: Checking for default variant on product:', product.id);
      const existingVariantsResponse = await this.graphqlService.query({
        query: `
        query getProductVariants($id: ID!) {
          product(id: $id) {
            id
            title
            variants(first: 1) {
              edges {
                node {
                  id
                  title
                  selectedOptions {
                    name
                    value
                  }
                }
              }
            }
          }
        }
      `, variables: { id: product.id } });

      const existingVariants = existingVariantsResponse?.product?.variants?.edges?.map(edge => edge.node) || [];
      const existingVariantCombinations = new Set();
      
      existingVariants.forEach(variant => {
        const optionCombo = variant.selectedOptions
          .map(opt => opt.value)
          .sort()
          .join('_');
        existingVariantCombinations.add(optionCombo);
        console.log(`Found existing variant: ${variant.title} with options: ${optionCombo}`);
      });
      
      // Step 4: Update the default variant instead of deleting it
      if (existingVariants.length > 0 && productInput.variants && productInput.variants.length > 0) {
        const defaultVariant = existingVariants[0];
        console.log('Found default variant:', defaultVariant.id);
        
        // Get the first variant we want to create (which should match the first option values)
        const firstDesiredVariant = productInput.variants.find(v => {
          // Find the variant with the first option values
          if (!v.optionValues) return false;
          
          // Check if this variant has the first option values from our options
          const hasFirstOptionValues = v.optionValues.every((ov, index) => {
            // Get the first value for this option from the productInput
            const firstValueForOption = productInput.options[index]?.values[0]?.name || 
                                       productInput.options[index]?.values[0] || 
                                       '';
            return ov.name === firstValueForOption;
          });
          
          return hasFirstOptionValues;

        });
        
        // If we can't find an exact match, just use the first variant
        const variantToUse = firstDesiredVariant || productInput.variants[0];
        
        if (variantToUse) {
          console.log('Updating default variant to match first variant:', {
            defaultVariantId: defaultVariant.id,
            desiredVariantSku: variantToUse.sku,
            desiredVariantOptions: variantToUse.optionValues.map(ov => ov.name).join('/')
          });
          
          // Extract inventory quantity from the variant
          let inventoryQuantity = 0;
          
          if (variantToUse.inventoryQuantity !== undefined) {
            inventoryQuantity = parseInt(variantToUse.inventoryQuantity, 10);
          } else if (variantToUse.inventory_quantity !== undefined) {
            inventoryQuantity = parseInt(variantToUse.inventory_quantity, 10);
          } else if (variantToUse.quantity !== undefined) {
            inventoryQuantity = parseInt(variantToUse.quantity, 10);
          } else if (typeof variantToUse.inventory === 'object' && variantToUse.inventory !== null) {
            // Try to extract from inventory object if it exists
            inventoryQuantity = parseInt(variantToUse.inventory.quantity || 0, 10);
          }
          
          // Ensure it's a number
          inventoryQuantity = isNaN(inventoryQuantity) ? 0 : inventoryQuantity;
          
          console.log(`Using inventory quantity: ${inventoryQuantity} for variant with SKU: ${variantToUse.sku}`);
          console.log('Variant data:', JSON.stringify(variantToUse, null, 2));
          
          // Update the default variant instead of deleting it
          const updateVariantMutation = `
            mutation productVariantsBulkUpdate($productId: ID!, $variants: [ProductVariantsBulkInput!]!) {
              productVariantsBulkUpdate(productId: $productId, variants: $variants) {
                product {
                  id
                }
                productVariants {
                  id
                  price
                  compareAtPrice
                  inventoryItem {
                    id
                    sku
                  }
                }
                userErrors {
                  field
                  message
                }
              }
            }
          `;
          
          const updateVariantResult = await this.graphqlService.mutate({
            query: updateVariantMutation,
            variables: {
              productId: product.id,
              variants: [{
                id: defaultVariant.id,
                price: variantToUse.price || '',
                compareAtPrice: variantToUse.compare_at_price || variantToUse.compareAtPrice,
                inventoryPolicy: "DENY"
              }]
            }
          });
          
          if (updateVariantResult?.productVariantsBulkUpdate?.userErrors?.length > 0) {
            const errors = updateVariantResult.productVariantsBulkUpdate.userErrors;
            console.warn('Warning: Could not update default variant:', errors);
            // Continue despite errors - we'll log them but not fail the whole creation
          } else {
            console.log('Successfully updated default variant');
            
            // Update inventory quantity for the default variant
            const updatedVariant = updateVariantResult.productVariantsBulkUpdate.productVariants[0];
            
            if (updatedVariant && updatedVariant.inventoryItem && updatedVariant.inventoryItem.id) {
              const defaultLocation = await this.inventoryService.getDefaultLocation();
              
              if (defaultLocation) {
                console.log(`Updating inventory quantity for default variant to ${inventoryQuantity}`);
                
                await this.inventoryService.updateInventoryQuantity(
                  updatedVariant.inventoryItem.id,
                  defaultLocation,
                  inventoryQuantity
                );
              }
            }
            
            // Update the SKU of the default variant using inventoryItemUpdate mutation
            if (updatedVariant.inventoryItem && updatedVariant.inventoryItem.id) {
              // Check for SKU in variant_updates first (from UI)
              let variantSku = null;
              
              // Find the matching variant in the original variants to get its ID
              const originalVariantId = this.findOriginalVariantId(productInput.originalVariants, variantToUse);
              
              console.log(`Looking for variant updates for original variant ID: ${originalVariantId}`);
              
              // Check if we have variant updates for this variant using the original ID
              if (changes && changes.variant_updates && originalVariantId && 
                  changes.variant_updates[originalVariantId] && 
                  changes.variant_updates[originalVariantId].sku) {
                // Use the SKU from UI updates
                variantSku = changes.variant_updates[originalVariantId].sku;
                console.log(`Using SKU from UI updates for default variant: ${variantSku}`);
              } else {
                // Fall back to variant's original SKU
                variantSku = variantToUse.inventoryItem?.sku || variantToUse.sku;
                console.log(`Using original SKU for default variant: ${variantSku}`);
              }
              
              if (variantSku) {
                console.log(`Updating SKU of default variant to ${variantSku}`);
                
                const updateSkuMutation = `
                  mutation inventoryItemUpdate($id: ID!, $input: InventoryItemInput!) {
                    inventoryItemUpdate(id: $id, input: $input) {
                      inventoryItem {
                        id
                        sku
                      }
                      userErrors {
                        field
                        message
                      }
                    }
                  }
                `;
                
                const updateSkuResult = await this.graphqlService.mutate({
                  query: updateSkuMutation,
                  variables: {
                    id: updatedVariant.inventoryItem.id,
                    input: {
                      sku: variantSku
                    }
                  }
                });
                
                if (updateSkuResult?.inventoryItemUpdate?.userErrors?.length > 0) {
                  console.error('Error updating SKU:', updateSkuResult.inventoryItemUpdate.userErrors);
                } else {
                  console.log(`Successfully updated SKU to ${variantSku}`);
                  // Update the updatedVariant object with the new SKU
                  updatedVariant.sku = variantSku;
                }
              }
            }
            
            // Add this variant to our list of created variants so it's included in further processing
            allCreatedVariants.push({
              ...updatedVariant,
              selectedOptions: defaultVariant.selectedOptions
            });
            
            // Remove the variant we used from our list of variants to create
            const usedVariantIndex = productInput.variants.findIndex(v => v === variantToUse);
            if (usedVariantIndex !== -1) {
              console.log(`Removing variant at index ${usedVariantIndex} from creation list as it's now the default variant`);
              productInput.variants.splice(usedVariantIndex, 1);
            }
          }
        } else {
          console.log('Could not find a matching first variant to update the default variant with');
        }
      }
      
      // Step 5: Create all variants in bulk, avoiding duplicates
      if (productInput.variants && productInput.variants.length > 0) {
        console.log('Step 5: Creating variants in bulk:', {
          productId: product.id,
          variantCount: productInput.variants.length
        });

        // Use the variants directly from the product input
        console.log(`Creating ${productInput.variants.length} variants directly from UI data`);
        
        const newlyCreatedVariants = await this.createProductVariantsInChunks(
          product.id,
          productInput.variants,
          productInput
        );
        
        // Add the newly created variants to our list of created variants
        allCreatedVariants.push(...newlyCreatedVariants);

        console.log(`Successfully created all ${newlyCreatedVariants.length} additional variants`);
      }
      
      // Step 6: Upload media/images
      let uploadedMedia = [];
      if (productInput.images && productInput.images.length > 0) {
        console.log('Step 6: Uploading images and linking to variants');
        console.log('Extracted images:', productInput.images);
        
        // Use the enhanced image uploading and linking method if we have variants
        if (allCreatedVariants.length > 0) {
          console.log('Using enhanced image uploading with variant linking');
          
          // Make sure the tracking service is properly initialized
          if (this.variantImageTrackingService) {
            console.log('Initializing variant image tracking service');
            // Create a product object with variants for the tracking service
            const originalProduct = {
              variants: productInput.originalVariants
            };
            
            // Initialize the tracking service with the product and images
            this.variantImageTrackingService.initializeTracking(originalProduct, productInput.images);
          }
          
          uploadedMedia = await this.mediaService.uploadImagesAndLinkToVariants(
            product.id, 
            productInput.images, 
            allCreatedVariants, 
            this.variantImageTrackingService
          );
          console.log(`Successfully uploaded ${uploadedMedia.length} media files with variant linking`);
        } else {
          // Fall back to regular image upload if no variants
          console.log('No variants created, using standard image upload');
          const mediaItems = productInput.images.map(image => ({
            originalSource: image.src || image.filename || "default_image_source", // Provide a default if null
            alt: productInput.imageAltText || "", // Provide an empty string if null
            mediaContentType: "IMAGE" // Ensure mediaContentType is specified
          }));

          uploadedMedia = await this.mediaService.uploadProductMedia(product.id, mediaItems);
          console.log(`Successfully uploaded ${uploadedMedia.length} media files`);
        }
      }

      // Step 7: Associate media with variants (only needed if not using enhanced upload method)
      if (uploadedMedia.length > 0 && allCreatedVariants.length > 0 && !this.variantImageTrackingService) {
        console.log('Step 7: Associating media with variants');
        
        // Use our variant image tracking service to get associations
        let variantMediaAssociations = [];
        
        // First try using our tracking service if available
        if (this.variantImageTrackingService) {
          console.log('Using variant image tracking service for image associations');
          variantMediaAssociations = this.variantImageTrackingService.getImageVariantAssociations(uploadedMedia, allCreatedVariants);
          console.log(`Generated ${variantMediaAssociations.length} associations using tracking service`);
        }
        
        // If no associations were found and we have variant image associations in the input, use those
        if (variantMediaAssociations.length === 0 && productInput.variantImageAssociations) {
          console.log('Using variant image associations from product input');
          variantMediaAssociations = productInput.variantImageAssociations;
          console.log(`Using ${Object.keys(variantMediaAssociations).length} associations from input`);
        }
        
        // If we have associations, apply them
        if (variantMediaAssociations.length > 0) {
          await this.mediaService.associateMediaWithVariants(product.id, variantMediaAssociations);
          console.log('Successfully associated media with variants');
        } else {
          console.log('No variant-media associations to apply');
        }
      } else if (productInput.variantImageAssociations) {
        console.log('Step 7: Associating media with variants using existing associations');
        await this.mediaService.associateMediaWithVariants(product.id, productInput.variantImageAssociations);
        console.log('Successfully associated media with variants using existing associations');
      }
      
      // Return a success object with all the relevant data
      return {
        success: true,
        productId: product.id,
        variantIds: allCreatedVariants.map(variant => variant.id) || [],
        mediaIds: uploadedMedia.map(media => media.id) || []
      };
    } catch (error) {
      console.error('Error creating product:', error);
      return {
        success: false,
        error: error.message || 'Unknown error creating product'
      };
    }
  }

  /**
   * Creates product variants in chunks to avoid API limits
   * @param {string} productId - The ID of the product
   * @param {Array} variants - Array of variants to create
   * @param {Object} productInput - Product input data
   * @returns {Promise<Array>} - Array of created variants
   */
  async createProductVariantsInChunks(productId, variants, productInput = {}) {
    console.log(`Creating ${variants.length} variants for product ${productId}`);
    
    if (!variants || variants.length === 0) {
      console.log('No variants to create');
      return [];
    }
    
    // Ensure we have the correct case for option values
    // Create a map of lowercase option values to their correctly cased versions from product options
    let optionValueCaseMap = new Map();
    if (productInput && productInput.options && Array.isArray(productInput.options)) {
      productInput.options.forEach(option => {
        if (option.name && option.values && Array.isArray(option.values)) {
          // For each option value, create a mapping of lowercase -> correct case
          const valueMap = new Map();
          option.values.forEach(value => {
            if (typeof value === 'string') {
              valueMap.set(value.toLowerCase(), value);
            }
          });
          optionValueCaseMap.set(option.name, valueMap);
          console.log(`Created case mapping for option ${option.name} in createProductVariantsInChunks:`, [...valueMap.entries()]);
        }
      });
    }
    
    // Correct the case of option values in each variant
    const correctedVariants = variants.map(variant => {
      const correctedVariant = { ...variant };
      
      // Fix option values case
      if (correctedVariant.optionValues && Array.isArray(correctedVariant.optionValues)) {
        correctedVariant.optionValues = correctedVariant.optionValues.map(optionValue => {
          const correctedOptionValue = { ...optionValue };
          
          // If we have a case mapping for this option, use it
          if (optionValue.optionName && optionValue.name && 
              optionValueCaseMap.has(optionValue.optionName)) {
            const valueMap = optionValueCaseMap.get(optionValue.optionName);
            if (valueMap.has(optionValue.name.toLowerCase())) {
              const correctCaseValue = valueMap.get(optionValue.name.toLowerCase());
              console.log(`Using correctly cased value '${correctCaseValue}' instead of '${optionValue.name}' for option '${optionValue.optionName}'`);
              correctedOptionValue.name = correctCaseValue;
            }
          }
          
          return correctedOptionValue;
        });
      }
      
      // Fix options array case if present
      if (correctedVariant.options && Array.isArray(correctedVariant.options)) {
        correctedVariant.options = correctedVariant.options.map(option => {
          const correctedOption = { ...option };
          
          // If we have a case mapping for this option, use it
          if (option.option && option.value && 
              optionValueCaseMap.has(option.option)) {
            const valueMap = optionValueCaseMap.get(option.option);
            if (valueMap.has(option.value.toLowerCase())) {
              const correctCaseValue = valueMap.get(option.value.toLowerCase());
              console.log(`Using correctly cased value '${correctCaseValue}' instead of '${option.value}' for option '${option.option}'`);
              correctedOption.value = correctCaseValue;
            }
          }
          
          return correctedOption;
        });
      }
      
      return correctedVariant;
    });
    
    // Prepare variant inputs using the variant handler service
    console.log(`Preparing variant inputs for ${correctedVariants.length} variants`);
    const variantInputs = [];
    
    // Process each variant individually
    for (const variant of correctedVariants) {
      try {
        const variantInput = await this.variantHandlerService.prepareVariantInput(variant, productId);
        if (variantInput && Object.keys(variantInput).length > 0) {
          variantInputs.push(variantInput);
        }
      } catch (error) {
        console.error(`Error preparing variant input: ${error.message}`);
      }
    }
    
    console.log(`Prepared ${variantInputs.length} variant inputs for API submission`);
    
    // Create variants in chunks of 10 to avoid API limits
    const chunkSize = 10;
    const createdVariants = [];
    
    for (let i = 0; i < variantInputs.length; i += chunkSize) {
      const chunk = variantInputs.slice(i, i + chunkSize);
      console.log(`Creating chunk ${Math.floor(i / chunkSize) + 1} of ${Math.ceil(variantInputs.length / chunkSize)} (${chunk.length} variants)`);
      
      try {
        // First try to create variants in bulk
        const result = await this.variantHandlerService.createProductVariants(productId, chunk);
        
        if (result && result.variants) {
          console.log(`Successfully created ${result.variants.length} variants in bulk`);
          createdVariants.push(...result.variants);
        } else {
          // If bulk creation fails, create variants individually
          console.log('Bulk variant creation failed, falling back to individual creation');
          const individualResults = await this.variantHandlerService._createVariantsIndividually(productId, chunk);
          
          if (individualResults && individualResults.variants) {
            console.log(`Successfully created ${individualResults.variants.length} variants individually`);
            createdVariants.push(...individualResults.variants);
          }
        }
      } catch (error) {
        console.error(`Error creating variant chunk ${Math.floor(i / chunkSize) + 1}:`, error);
        
        // Try to create variants individually as a fallback
        try {
          console.log('Attempting to create variants individually after chunk error');
          const individualResults = await this.variantHandlerService._createVariantsIndividually(productId, chunk);
          
          if (individualResults && individualResults.variants) {
            console.log(`Successfully created ${individualResults.variants.length} variants individually after error`);
            createdVariants.push(...individualResults.variants);
          }
        } catch (individualError) {
          console.error('Error creating variants individually:', individualError);
        }
      }
    }
    
    console.log(`Created a total of ${createdVariants.length} variants`);
    return createdVariants;
  }

  /**
   * Archives a product in Shopify
   * @param {string} productId - Product ID to archive
   * @returns {Promise<object>} - Archived product data
   */
  async archiveProduct(productId) {
    // Validate product ID
    if (!productId || productId === '0' || !productId.startsWith('gid://')) {
      console.warn(`Invalid product ID for archiving: ${productId}`);
      return { id: productId, status: 'INVALID_ID' };
    }

    const result = await this.graphqlService.makeGraphQLRequest({ query: `
      mutation productUpdate($input: ProductInput!) {
        productUpdate(input: $input) {
          product {
            id
            status
          }
          userErrors {
            field
            message
          }
        }
      }
    `, variables: { 
      input: {
        id: productId,
        status: "ARCHIVED"
      } 
    } });

    if (result?.productUpdate?.userErrors?.length > 0) {
      const errors = result.productUpdate.userErrors;
      console.error('Error archiving product:', errors);
      throw new Error(errors.map(e => `${e.field}: ${e.message}`).join(', '));
    }

    return result.productUpdate.product;
  }

  /**
   * Deletes a product in Shopify
   * @param {string} productId - Product ID to delete
   * @returns {Promise<boolean>} - Success status
   */
  async deleteProduct(productId) {
    const result = await this.graphqlService.makeGraphQLRequest({ query: `
      mutation productDelete($input: ProductDeleteInput!) {
        productDelete(input: $input) {
          deletedProductId
          userErrors {
            field
            message
          }
        }
      }
    `, variables: { 
      input: {
        id: productId
      } 
    } });

    if (result?.productDelete?.userErrors?.length > 0) {
      const errors = result.productDelete.userErrors;
      console.error('Error deleting product:', errors);
      throw new Error(errors.map(e => `${e.field}: ${e.message}`).join(', '));
    }

    return !!result.productDelete.deletedProductId;
  }

  /**
   * Processes a batch of products sequentially
   * @param {Array<object>} products - Products to process
   * @param {object} localEdits - Local edits to apply
   * @param {object} options - Processing options
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<object>} - Processing results
   */
  async processProductsSequentially(products, localEdits = {}, options = {}, onProgress = () => {}) {
    const results = {
      success: [],
      errors: []
    };

    const productIds = Object.keys(products);

    for (let i = 0; i < productIds.length; i++) {
      const productId = productIds[i];
      const product = products[productId];

      try {
        onProgress({ 
          current: i + 1, 
          total: productIds.length, 
          message: `Creating product ${i + 1} of ${productIds.length}` 
        });

        // Get the local edits for this product - try both the full ID and the numeric ID
        // This handles cases where the UI uses a different ID format than the API
        let productEdits = {};
        if (localEdits[productId]) {
          productEdits = localEdits[productId];
        } else if (product.id && localEdits[product.id]) {
          productEdits = localEdits[product.id];
        } else {
          // Try to extract the numeric ID from the Shopify ID
          const numericId = product.id?.split('/').pop();
          if (numericId && localEdits[numericId]) {
            productEdits = localEdits[numericId];
          }
        }
        
        console.log(`Processing product ${productId} with local edits:`, JSON.stringify(productEdits, null, 2));

        // Add to queue to respect API rate limits
        const productInput = this.prepareProductInput(product, productEdits);

        // Get metafield count for progress message
        const metafieldCount = productInput.metafields?.length || 0;

        const createResult = await QueueManagerService.addToQueue(async () => this.createProduct(productInput, productEdits));
        
        // Check if product creation was successful before proceeding
        if (!createResult.success) {
          results.errors.push({
            productId,
            error: createResult.error || 'Unknown error creating product'
          });
          onProgress({ 
            current: i + 1, 
            total: productIds.length, 
            message: `Error: Failed to create product (${i + 1}/${productIds.length}): ${createResult.error}` 
          });
          continue; // Skip to next product without archiving/deleting the original
        }

        onProgress({ 
          current: i + 1, 
          total: productIds.length, 
          message: options.deleteOldProducts ? 
            `Deleting original product ${i + 1} of ${productIds.length}` : 
            `Archiving original product ${i + 1} of ${productIds.length}` 
        });

        // Archive or delete the old product
        if (options.deleteOldProducts) {
          // Ensure we have a valid Shopify product ID (should start with gid://)
          const shopifyProductId = product.id && product.id.startsWith('gid://') ? product.id : productId;
          console.log(`Deleting product with ID: ${shopifyProductId}`);
          await QueueManagerService.addToQueue(() => this.deleteProduct(shopifyProductId));
        } else {
          // Ensure we have a valid Shopify product ID (should start with gid://)
          const shopifyProductId = product.id && product.id.startsWith('gid://') ? product.id : productId;
          console.log(`Archiving product with ID: ${shopifyProductId}`);
          await QueueManagerService.addToQueue(() => this.archiveProduct(shopifyProductId));
        }

        results.success.push({
          oldProductId: productId,
          newProductId: createResult.productId,
          variantIds: createResult.variantIds || []
        });

        onProgress({ 
          current: i + 1, 
          total: productIds.length, 
          message: `Successfully created product ${i + 1} of ${productIds.length}${options.deleteOldProducts ? ' and deleted original' : ' and archived original'}${metafieldCount > 0 ? ` (including ${metafieldCount} metafields)` : ''}` 
        });
      } catch (error) {
        console.error(`Error processing product ${productId}:`, error);
        results.errors.push({
          productId,
          error: error.message
        });
        onProgress({ 
          current: i + 1, 
          total: productIds.length, 
          message: `Error: ${error.message} (${i + 1}/${productIds.length})` 
        });
      }

      // Add a small delay between products to avoid overwhelming the API
      if (i < productIds.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    return results;
  }

  /**
   * Logs an error with additional context
   * @param {string} method - Method where the error occurred
   * @param {Error} error - Error object
   * @param {object} context - Additional context
   * @returns {object} - Formatted error object
   */
  logError(method, error, context = {}) {
    console.error(`[ShopifyProductCreateService] Error in ${method}:`, error);
    console.error('Context:', JSON.stringify(context));

    // Capture stack trace if available
    if (error.stack) {
      console.error('Stack trace:', error.stack);
    }

    // Log additional details for specific error types
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }

    // Return a formatted error object that can be displayed to the user
    return {
      message: error.message || 'An unknown error occurred',
      method,
      context: JSON.stringify(context),
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Uploads product media (images) to Shopify
   * @param {string} productId - The ID of the product
   * @param {Array} mediaInputs - Array of media inputs
   * @returns {Promise<Array>} Created media objects
   */
  async uploadProductMedia(productId, mediaInputs) {
    try {
      // Use the mediaInputs directly instead of fetching product images
      if (!mediaInputs || mediaInputs.length === 0) {
        console.log('No media inputs provided for upload');
        return [];
      }

      // Ensure all required fields are present
      const validMediaItems = mediaInputs.filter(item => {
        if (!item.originalSource) {
          console.error('Missing originalSource in media item:', item);
          return false;
        }
        if (!item.mediaContentType) {
          console.error('Missing mediaContentType in media item:', item);
          return false;
        }
        return true;
      });

      if (validMediaItems.length === 0) {
        console.error('No valid media items to upload after filtering');
        return [];
      }

      console.log('Uploading media items:', validMediaItems);
      return await this.mediaService.uploadProductMedia(productId, validMediaItems);
    } catch (error) {
      console.error('Error uploading product media:', error);
      throw error;
    }
  }

  /**
   * Checks if media is ready (status is SUCCESS)
   * @param {string} mediaId - The ID of the media to check
   * @param {number} maxAttempts - Maximum number of attempts to check status
   * @returns {Promise<boolean>} Whether media is ready
   */
  async waitForMediaReady(mediaId, maxAttempts = 10) {
    try {
      return await this.mediaService.waitForMediaReady(mediaId, maxAttempts);
    } catch (error) {
      console.error('Error waiting for media ready:', error);
      throw error;
    }
  }

  /**
   * Associates media with variants
   * @param {string} productId - The ID of the product
   * @param {Array} variantMedia - Array of variant-media associations
   * @returns {Promise<object>} Result of the association
   */
  async associateMediaWithVariants(productId, variantMedia) {
    try {
      return await this.mediaService.associateMediaWithVariants(productId, variantMedia);
    } catch (error) {
      console.error('Error associating media with variants:', error);
      throw error;
    }
  }

  /**
   * Adds a single image to a product
   * @param {string} productId - The ID of the product
   * @param {object} imageInput - Image input data
   * @returns {Promise<object>} Created image data
   */
  async addImageToProduct(productId, imageInput) {
    try {
      return await this.mediaService.addImageToProduct(productId, imageInput);
    } catch (error) {
      console.error('Error adding image to product:', error);
      throw error;
    }
  }

  /**
   * Fetches product images
   * @param {string} productId - The ID of the product
   * @returns {Promise<Array>} Product images
   */
  async fetchProductImages(productId) {
    try {
      if (!productId) {
        throw new Error('Product ID is required to fetch images');
      }

      console.log(`Fetching images for product ID: ${productId}`);
      const query = `
        query getProductImages($id: ID!) {
          product(id: $id) {
            id
            title
            images(first: 10) {
              edges {
                node {
                  id
                  altText
                  originalSrc
                }
              }
            }
          }
        }
      `;

      const variables = { id: productId };
      console.log('GraphQL Request:', { query, variables });
      console.log('Data object for GraphQL request:', { query, variables });

      const result = await this.graphqlService.makeGraphQLRequest({ query, variables });
      console.log('GraphQL Response:', result);

      const images = result?.product?.images?.edges?.map(edge => edge.node) || [];

      return images;
    } catch (error) {
      console.error('Error fetching product images:', error);
      throw error;
    }
  }

  /**
   * Prepares variant data for API submission
   * @param {Object} variant - Original variant data
   * @param {Object} variantUpdates - UI updates for the variant
   * @param {Array} productOptions - Product options
   * @param {Object} productInput - Product input data
   * @returns {Object} Prepared variant data
   * @private
   */
  _prepareVariantData(variant, variantUpdates, productOptions, productInput = {}) {
    return this.variantHandlerService.prepareVariantData(variant, variantUpdates, productOptions, productInput);
  }

  /**
   * Parses inventory quantity from a variant object
   * @param {Object} variant - The variant object
   * @returns {number} The parsed inventory quantity
   * @private
   */
  _parseInventoryQuantity(variant) {
    return this.variantHandlerService.parseInventoryQuantity(variant);
  }
  
  /**
   * Formats option values for API submission
   * @param {Object} variant - The variant object
   * @param {Array} productOptions - Product options with their IDs
   * @param {Object} productInput - The product input data containing option value updates
   * @returns {Array} Formatted option values
   * @private
   */
  _formatOptionValues(variant, productOptions, productInput = {}) {
    return this.variantHandlerService.formatOptionValues(variant, productOptions, productInput);
  }

  /**
   * Generate all possible variant combinations from product options
   * @param {Array} productOptions - Array of product options
   * @param {Object} originalVariant - Optional original variant to use as a template for values
   * @param {Object} productInput - Product input data
   * @returns {Array} - Array of variant objects with all possible combinations
   */
  generateVariantsFromOptions(productOptions, originalVariant = null, productInput = {}) {
    console.log('Generating all possible variant combinations from options');
    
    if (!productOptions || !Array.isArray(productOptions) || productOptions.length === 0) {
      console.log('No product options provided, cannot generate variants');
      return [];
    }
    
    // Check if we have updated option values in productInput
    if (productInput && productInput.options && productInput.options.length > 0) {
      console.log('Using updated option values from product input for variant generation');
      
      // Map original option names to updated values
      const updatedOptionMap = new Map();
      
      // Build a map of option name -> updated values
      productInput.options.forEach(option => {
        if (option.name && option.values && Array.isArray(option.values)) {
          updatedOptionMap.set(option.name, option.values);
          console.log(`Mapped option ${option.name} to updated values:`, option.values);
        }
      });
      
      // Update the option values with the values from productInput
      productOptions = productOptions.map(option => {
        const updatedValues = updatedOptionMap.get(option.name);
        
        if (updatedValues) {
          console.log(`Using updated values for option ${option.name}:`, updatedValues);
          return {
            ...option,
            values: updatedValues
          };
        }
        
        return option;
      });
    }
    
    // Normalize option values
    const normalizedOptions = productOptions.map(option => {
      // Ensure option has values array
      const values = option.values && Array.isArray(option.values) ? 
        option.values : ['Default'];
      
      return {
        name: option.name,
        values: values.map(value => {
          // Normalize value to string if it's an object
          return typeof value === 'object' ? 
            (value.name || value.value || 'Default') : 
            String(value);
        })
      };
    });
    
    console.log(`Found ${normalizedOptions.length} options with values:`, JSON.stringify(normalizedOptions.map(o => `${o.name} (${o.values.length})`)));
    
    // Calculate total number of variants
    const totalVariants = normalizedOptions.reduce((total, option) => {
      return total * (option.values ? option.values.length : 1);
    }, 1);
    
    console.log(`Will generate ${totalVariants} variants from all combinations`);
    
    // Generate all combinations
    const variants = [];
    
    // Helper function to generate combinations recursively
    const generateCombinations = (currentIndex, currentCombination) => {
      if (currentIndex === normalizedOptions.length) {
        // We've reached the end of options, add the variant
        const variant = {};
        
        // Set option values
        currentCombination.forEach((value, index) => {
          variant[`option${index + 1}`] = value;
        });
        
        // Copy properties from original variant if available
        if (originalVariant) {
          variant.price = originalVariant.price || '0.00';
          variant.compareAtPrice = originalVariant.compareAtPrice || '';
          variant.inventoryQuantity = originalVariant.inventoryQuantity || 0;
          variant.inventory_quantity = originalVariant.inventory_quantity || 0;
          variant.sku = originalVariant.sku || '';
          variant.barcode = originalVariant.barcode || '';
          variant.weight = originalVariant.weight || 0;
          variant.weightUnit = originalVariant.weightUnit || 'KILOGRAMS';
        } else {
          // Default values if no original variant
          variant.price = '0.00';
          variant.compareAtPrice = '';
          variant.inventoryQuantity = 0;
          variant.inventory_quantity = 0;
          variant.sku = `VARIANT-${variants.length + 1}-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
          variant.barcode = '';
          variant.weight = 0;
          variant.weightUnit = 'KILOGRAMS';
        }
        
        // Add variant to the list
        variants.push(variant);
        return;
      }
      
      const currentOption = normalizedOptions[currentIndex];
      const values = currentOption.values;
      
      // For each value of the current option
      for (let i = 0; i < values.length; i++) {
        const optionValue = values[i];
        
        // Add this option value to the combination and continue recursively
        generateCombinations(
          currentIndex + 1, 
          [...currentCombination, optionValue]
        );
      }
    };
    
    // Start the recursive generation with empty combination
    generateCombinations(0, []);
    
    console.log(`Generated ${variants.length} variants with all possible combinations`);
    return variants;
  }

  /**
   * Generate all possible variants from product options
   * @param {Array} productOptions - Product options
   * @param {Object} productInput - Product input data
   * @returns {Array} - Array of variant objects with all possible combinations
   */
  generateAllPossibleVariants(productOptions, productInput = {}) {
    console.log('Generating all possible variants with updated option values');  
    
    // Check if we have updated option values in productInput
    if (productInput && productInput.options && productInput.options.length > 0) {
      console.log('Using updated option values from product input');
      
      // Map original option names to updated values
      const updatedOptionMap = new Map();
      
      // Build a map of option name -> updated values
      productInput.options.forEach(option => {
        if (option.name && option.values && Array.isArray(option.values)) {
          updatedOptionMap.set(option.name, option.values);
          console.log(`Mapped option ${option.name} to updated values:`, option.values);
        }
      });
      
      // Update the option values with the values from productInput
      productOptions = productOptions.map(option => {
        const updatedValues = updatedOptionMap.get(option.name);
        
        if (updatedValues) {
          console.log(`Using updated values for option ${option.name}:`, updatedValues);
          return {
            ...option,
            values: updatedValues
          };
        }
        
        return option;
      });
    }
    
    const totalVariants = productOptions.reduce((total, option) => {
      return total * (option.values ? option.values.length : 1);
    }, 1);
    
    console.log(`Will generate ${totalVariants} variants from all combinations`);
    
    // Generate all combinations
    const variants = [];
    
    // Helper function to generate combinations recursively
    const generateCombinations = (currentIndex, currentCombination, currentOptionValues) => {
      if (currentIndex === productOptions.length) {
        // We've reached the end of options, add the variant
        // Create a unique SKU by combining variant number with timestamp and random number
        const variantNumber = variants.length + 1;
        const sku = `VARIANT-${variantNumber}-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
        
        variants.push({
          // Store options in the format expected by _createOptionKey
          options: currentCombination,
          // Store optionValues as an array of objects for the Shopify API
          optionValues: currentOptionValues,
          price: '0.00',
          compareAtPrice: '',
          inventoryQuantity: 0,
          inventory_quantity: 0,
          sku: sku
        });
        return;
      }
      
      const currentOption = productOptions[currentIndex];
      const values = currentOption.values;
      
      // For each value of the current option
      for (let i = 0; i < values.length; i++) {
        const optionValue = values[i];
        
        // Add this option value to the combination and continue recursively
        generateCombinations(
          currentIndex + 1, 
          [
            ...currentCombination,
            {
              option: currentOption.name,
              value: optionValue
            }
          ],
          [
            ...currentOptionValues,
            {
              option: currentOption.name,
              value: optionValue
            }
          ]
        );
      }
    };
    
    // Start the recursive generation with empty combination
    generateCombinations(0, [], []);
    
    console.log(`Generated ${variants.length} variants with all possible combinations`);
    return variants;
  }

  /**
   * Creates a key based on option values for a variant
   * @param {Object} variant - The variant object
   * @param {Array} productOptions - Product options
   * @returns {string} - Key based on option values
   * @private
   */
  _createOptionKey(variant, productOptions) {
    if (!variant) return '';
    
    try {
      // If variant has options array, use that
      if (variant.options && Array.isArray(variant.options) && variant.options.length > 0) {
        return variant.options.map(option => {
          // Get the option value, handling both string and object formats
          const optionValue = typeof option === 'string' ? option : 
                             (option.originalValue || option.name || option.value || '');
          return optionValue.toLowerCase(); // Make case-insensitive for comparison
        }).join('-');
      }
      
      // If variant has option1, option2, option3 properties, use those
      if (variant.option1 || variant.option2 || variant.option3) {
        const options = [];
        
        if (variant.option1) options.push(variant.option1.toLowerCase());
        if (variant.option2) options.push(variant.option2.toLowerCase());
        if (variant.option3) options.push(variant.option3.toLowerCase());
        
        return options.join('-');
      }
      
      // If variant has optionValues array (Shopify API format), use that
      if (variant.optionValues && Array.isArray(variant.optionValues) && variant.optionValues.length > 0) {
        return variant.optionValues.map(option => {
          // Check for originalValue first to preserve case sensitivity
          const value = option.originalValue || option.name || '';
          return value.toLowerCase(); // Make case-insensitive for comparison
        }).join('-');
      }
      
      // If variant has selectedOptions (from Shopify API), use that
      if (variant.selectedOptions && Array.isArray(variant.selectedOptions) && variant.selectedOptions.length > 0) {
        return variant.selectedOptions.map(option => {
          // Check for originalValue first to preserve case sensitivity
          const value = option.originalValue || option.value || '';
          return value.toLowerCase(); // Make case-insensitive for comparison
        }).join('-');
      }
      
      // If we couldn't create a key, log and return empty string
      console.warn('Could not create option key for variant:', variant);
      return '';
    } catch (error) {
      console.error('Error creating option key:', error);
      return '';
    }
  }

  /**
   * Finds a similar variant with matching option values
   * @param {Object} variant - The variant to find a similar one for
   * @param {Array} existingVariants - Array of existing variants
   * @param {Array} productOptions - Product options
   * @returns {Object} - Similar variant or null if not found
   * @private
   */
  _findSimilarVariant(variant, existingVariants, productOptions) {
    if (!variant || !existingVariants || !Array.isArray(existingVariants) || existingVariants.length === 0) {
      return null;
    }
    
    try {
      console.log(`Finding similar variant for:`, JSON.stringify(variant));
      
      // First, try to find by variant index (most reliable)
      if (variant.index !== undefined) {
        console.log(`Looking for variant with index: ${variant.index}`);
        const indexMatch = existingVariants.find(existingVariant => {
          return existingVariant.index !== undefined && existingVariant.index === variant.index;
        });
        
        if (indexMatch) {
          console.log(`Found variant with matching index: ${variant.index}`);
          return indexMatch;
        }
      }
      
      // If no index match, try to find by metafield
      if (variant.metafields && Array.isArray(variant.metafields)) {
        const indexMetafield = variant.metafields.find(m => 
          m.namespace === "app" && m.key === "variant_index"
        );
        
        if (indexMetafield && indexMetafield.value) {
          console.log(`Looking for variant with metafield index: ${indexMetafield.value}`);
          const metafieldMatch = existingVariants.find(existingVariant => {
            if (existingVariant.metafields && Array.isArray(existingVariant.metafields)) {
              return existingVariant.metafields.some(m => 
                m.namespace === "app" && m.key === "variant_index" && m.value === indexMetafield.value
              );
            }
            return false;
          });
          
          if (metafieldMatch) {
            console.log(`Found variant with matching metafield index: ${indexMetafield.value}`);
            return metafieldMatch;
          }
        }
      }
      
      // If we still don't have a match, fall back to option values comparison
      // Get the option key for the variant we're looking for
      const variantKey = this._createOptionKey(variant, productOptions);
      console.log(`No index match found. Finding similar variant for key: ${variantKey}`);
      
      // Find a similar variant by comparing option keys
      return existingVariants.find(existingVariant => {
        const existingKey = this._createOptionKey(existingVariant, productOptions);
        
        // Check for exact match first (case-insensitive)
        if (variantKey === existingKey) {
          console.log(`Found exact match with key: ${existingKey}`);
          return true;
        }
        
        // Check for partial match (e.g., if one variant has 2 options and another has 3)
        const variantKeyParts = variantKey.split('-');
        const existingKeyParts = existingKey.split('-');
        
        // Count how many option values match
        const matchCount = variantKeyParts.filter((part, index) => {
          if (index >= existingKeyParts.length) return false;
          // Make case-insensitive comparison
          return part.toLowerCase() === existingKeyParts[index].toLowerCase();
        }).length;
        
        // Consider it a match if all available options match
        const minMatchRequired = Math.min(variantKeyParts.length, existingKeyParts.length);
        const isMatch = matchCount === minMatchRequired;
        
        if (isMatch) {
          console.log(`Found similar variant with key ${existingKey} matching ${matchCount}/${minMatchRequired} options`);
        }
        
        return isMatch;
      }) || null;
    } catch (error) {
      console.error('Error in _findSimilarVariant:', error);
      return null;
    }
  }

  /**
   * Helper function to validate URLs
   * @param {string} url - URL to validate
   * @returns {boolean} Whether the URL is valid
   */
  isValidUrl(url) {
    try {
      new URL(url);
      return true;
    } catch (_) {
      return false;
    }
  }

  /**
   * Creates variants for a product
   * @param {string} productId - Shopify product ID
   * @param {Array} variants - Array of variants to create
   * @param {Object} productInput - Product input data
   * @returns {Promise<Object>} - Created variants
   */
  async createProductVariants(productId, variants, productInput) {
    console.log(`Creating ${variants.length} variants for product ${productId}`);
    
    if (!variants || variants.length === 0) {
      console.log('No variants to create');
      return { variants: [] };
    }
    
    // Ensure we have the correct case for option values
    // Create a map of lowercase option values to their correctly cased versions from product options
    let optionValueCaseMap = new Map();
    if (productInput && productInput.options && Array.isArray(productInput.options)) {
      productInput.options.forEach(option => {
        if (option.name && option.values && Array.isArray(option.values)) {
          // For each option value, create a mapping of lowercase -> correct case
          const valueMap = new Map();
          option.values.forEach(value => {
            if (typeof value === 'string') {
              valueMap.set(value.toLowerCase(), value);
            }
          });
          optionValueCaseMap.set(option.name, valueMap);
          console.log(`Created case mapping for option ${option.name} in createProductVariants:`, [...valueMap.entries()]);
        }
      });
    }
    
    // Correct the case of option values in each variant
    const correctedVariants = variants.map(variant => {
      const correctedVariant = { ...variant };
      
      // Fix option values case
      if (correctedVariant.optionValues && Array.isArray(correctedVariant.optionValues)) {
        correctedVariant.optionValues = correctedVariant.optionValues.map(optionValue => {
          const correctedOptionValue = { ...optionValue };
          
          // If we have a case mapping for this option, use it
          if (optionValue.optionName && optionValue.name && 
              optionValueCaseMap.has(optionValue.optionName)) {
            const valueMap = optionValueCaseMap.get(optionValue.optionName);
            if (valueMap.has(optionValue.name.toLowerCase())) {
              const correctCaseValue = valueMap.get(optionValue.name.toLowerCase());
              console.log(`Using correctly cased value '${correctCaseValue}' instead of '${optionValue.name}' for option '${optionValue.optionName}'`);
              correctedOptionValue.name = correctCaseValue;
            }
          }
          
          return correctedOptionValue;
        });
      }
      
      // Fix options array case if present
      if (correctedVariant.options && Array.isArray(correctedVariant.options)) {
        correctedVariant.options = correctedVariant.options.map(option => {
          const correctedOption = { ...option };
          
          // If we have a case mapping for this option, use it
          if (option.option && option.value && 
              optionValueCaseMap.has(option.option)) {
            const valueMap = optionValueCaseMap.get(option.option);
            if (valueMap.has(option.value.toLowerCase())) {
              const correctCaseValue = valueMap.get(option.value.toLowerCase());
              console.log(`Using correctly cased value '${correctCaseValue}' instead of '${option.value}' for option '${option.option}'`);
              correctedOption.value = correctCaseValue;
            }
          }
          
          return correctedOption;
        });
      }
      
      return correctedVariant;
    });
    
    try {
      // Use the variant handler service to create the variants
      const variantHandlerService = this.serviceContainer.get('ShopifyVariantHandlerService');
      const result = await variantHandlerService.createProductVariants(productId, correctedVariants, productInput);
      
      if (result && result.variants) {
        console.log(`Successfully created ${result.variants.length} variants`);
        return result;
      } else {
        console.error('Error creating variants: No variants returned');
        return { variants: [] };
      }
    } catch (error) {
      console.error('Error creating variants:', error);
      return { variants: [] };
    }
  }

  /**
   * Fetches variant-image associations from the Shopify API
   * @param {string} productId - The ID of the product
   * @returns {Promise<Object>} - Object containing variant-image associations
   */
  async fetchVariantImageAssociations(productId) {
    console.log(`Fetching variant-image associations for product ${productId}`);
    
    try {
      // Query the Shopify API to get the product with variants and images
      const query = `
        query getProductWithVariantImages($id: ID!) {
          product(id: $id) {
            id
            images(first: 50) {
              edges {
                node {
                  id
                  url
                  altText
                  width
                  height
                  variants {
                    id
                  }
                }
              }
            }
            variants(first: 50) {
              edges {
                node {
                  id
                  title
                  selectedOptions {
                    name
                    value
                  }
                }
              }
            }
          }
        }
      `;
      
      const variables = { id: productId };
      
      const response = await this.graphqlService.makeRequest(query, variables);
      
      if (!response || !response.product) {
        console.log('Failed to fetch product variant-image associations');
        return null;
      }
      
      // Process the response to extract variant-image associations
      const product = response.product;
      const images = product.images.edges.map(edge => edge.node);
      const variants = product.variants.edges.map(edge => edge.node);
      
      console.log(`Found ${images.length} images and ${variants.length} variants`);
      
      // Create a map of image IDs to variant IDs
      const imageToVariantsMap = new Map();
      
      // First, process direct image-variant associations from the images
      images.forEach(image => {
        if (image.variants && image.variants.length > 0) {
          const variantIds = image.variants.map(variant => variant.id);
          imageToVariantsMap.set(image.id, variantIds);
          console.log(`Image ${image.id} is directly associated with ${variantIds.length} variants`);
        }
      });
      
      // Then, process variant-image associations from the variants
      variants.forEach(variant => {
        if (variant.image) {
          const imageId = variant.image.id;
          if (!imageToVariantsMap.has(imageId)) {
            imageToVariantsMap.set(imageId, []);
          }
          
          const variantIds = imageToVariantsMap.get(imageId);
          if (!variantIds.includes(variant.id)) {
            variantIds.push(variant.id);
            console.log(`Added variant ${variant.id} to image ${imageId} from variant data`);
          }
        }
      });
      
      // Convert the map to an array of objects for easier processing
      const associations = [];
      imageToVariantsMap.forEach((variantIds, imageId) => {
        associations.push({
          imageId,
          variantIds
        });
      });
      
      console.log(`Extracted ${associations.length} image-variant associations`);
      
      return {
        images,
        variants,
        associations
      };
    } catch (error) {
      console.error('Error fetching variant-image associations:', error);
      return null;
    }
  }

  findOriginalVariantId(originalVariants, variant) {
    if (!originalVariants || !Array.isArray(originalVariants) || originalVariants.length === 0) {
      return null;
    }

    // Try to find the variant by its SKU
    const skuMatch = originalVariants.find(v => v.sku === variant.sku);
    if (skuMatch) {
      console.log(`Found original variant by SKU: ${variant.sku}`);
      return skuMatch.id;
    }

    // Try to find the variant by its option values
    const optionMatch = originalVariants.find(v => {
      if (!v.selectedOptions || !Array.isArray(v.selectedOptions)) return false;
      if (!variant.optionValues || !Array.isArray(variant.optionValues)) return false;

      // Check if the option values match
      return v.selectedOptions.every((option, index) => {
        return option.value === variant.optionValues[index].name;
      });
    });

    if (optionMatch) {
      console.log(`Found original variant by option values: ${JSON.stringify(variant.optionValues)}`);
      return optionMatch.id;
    }

    // If we couldn't find a match, return null
    console.log(`Could not find original variant for: ${JSON.stringify(variant)}`);
    return null;
  }

  /**
   * Finds the original variant ID by matching a variant with the original variants
   * @param {Array} originalVariants - Array of original variants from the product
   * @param {Object} variant - The variant to find the original ID for
   * @returns {String|null} - The original variant ID or null if not found
   */
  findOriginalVariantId(originalVariants, variant) {
    if (!originalVariants || !Array.isArray(originalVariants) || originalVariants.length === 0) {
      console.log('No original variants to match against');
      return null;
    }

    // Try to find the variant by its option values (most reliable method)
    if (variant.optionValues && Array.isArray(variant.optionValues)) {
      const optionValuesForLog = variant.optionValues.map(ov => ov.name).join('/');
      console.log(`Looking for variant with option values: ${optionValuesForLog}`);
      
      const optionMatch = originalVariants.find(v => {
        if (!v.selectedOptions || !Array.isArray(v.selectedOptions)) return false;
        
        // Check if the number of options matches
        if (v.selectedOptions.length !== variant.optionValues.length) return false;
        
        // Check if all option values match (case insensitive)
        const allOptionsMatch = v.selectedOptions.every(so => {
          // Find matching option in variant.optionValues
          return variant.optionValues.some(ov => 
            ov.name.toLowerCase() === so.value.toLowerCase());
        });
        
        return allOptionsMatch;
      });

      if (optionMatch) {
        console.log(`Found original variant by option values: ${optionMatch.id}`);
        return optionMatch.id;
      }
    }

    // Try to find the variant by its SKU as a fallback
    if (variant.sku) {
      console.log(`Looking for variant with SKU: ${variant.sku}`);
      
      const skuMatch = originalVariants.find(v => {
        const originalSku = v.inventoryItem?.sku || v.sku;
        return originalSku === variant.sku;
      });
      
      if (skuMatch) {
        console.log(`Found original variant by SKU: ${skuMatch.id}`);
        return skuMatch.id;
      }
    }

    // If all else fails, try to find a variant with the same position
    if (variant.position) {
      const positionMatch = originalVariants.find(v => v.position === variant.position);
      if (positionMatch) {
        console.log(`Found original variant by position: ${positionMatch.id}`);
        return positionMatch.id;
      }
    }

    // If we still couldn't find a match, use the first variant as a last resort
    if (originalVariants.length > 0) {
      console.log(`Could not find exact match, using first variant as fallback: ${originalVariants[0].id}`);
      return originalVariants[0].id;
    }

    // If we couldn't find any match, return null
    console.log(`Could not find any matching original variant`);
    return null;
  }
}
